/**
 * TODO: package.xml and setup.py use maintainer email, maintainer name
 * and package description. These need to be the same across both files.
 * Update this when account details are added to auto generate these according
 * to account
 */

import JSZip from 'jszip';
import {
  toCamelCase,
  toSnakeCase
} from '@/modules/globalModule/components/helperMethods';
import { Node } from '../modules/nodesCanvasModule/components/storeClasses/node';
import { Service } from '../modules/nodesCanvasModule/components/storeClasses/service';
import { PackageT } from '@/modules/nodesCanvasModule/components/storeClasses/packageT';

import { V4UUID } from '@/firebase/database';
import {
  getNodeDependencies,
  getNodeDependency,
  getPackageDependencies
} from './dependenciesGetter';

/**
 * Initialises a ROS 2 package, creates the setup.py (if python package),
 * CMakeLists.txt (if c++ package), or ament_cmake_python (if both)
 * as well as the src, launch and include directories.
 * @param {JSZip} zip - empty JSZip to initialise package in
 * @param {PackageT} pkg - package to initialise
 */
export const initROSPackage = (
  zip: JSZip,
  workspaceId: V4UUID,
  pkg: PackageT
) => {
  createPackageXml(zip, pkg, workspaceId);
  const language = getPackageLanguages(pkg);
  if (language === 2) {
    createCMakeLists(zip, pkg, workspaceId);
    zip.folder('launch');
    let include: JSZip | null = zip.folder('include');
    if (include) include.folder(toSnakeCase(pkg.name));
    zip.folder('src');
  } else if (language === 1) {
    createSetupPy(zip, pkg);
    createSetupCfg(zip, pkg);
    let resource: JSZip | null = zip.folder('resource');
    let wsFolder: JSZip | null = zip.folder(toSnakeCase(pkg.name));
    if (resource) resource.file(toSnakeCase(pkg.name), '');
    if (wsFolder) wsFolder.file('__init__.py', '');
  } else {
    createCMakeLists(zip, pkg, workspaceId);
    zip.folder('launch');
    let include: JSZip | null = zip.folder('include');
    let resource: JSZip | null = zip.folder('resource');
    let wsFolder: JSZip | null = zip.folder(toSnakeCase(pkg.name));
    if (include) include.folder(toSnakeCase(pkg.name));
    if (resource) resource.file(toSnakeCase(pkg.name), '');
    if (wsFolder) wsFolder.file('__init__.py', '');
  }
};

/**
 * figuring out if package has all python, all c++, or both
 * 2 - all cpp
 * 1 - all python (no services)
 * 0 - both
 */
export const getPackageLanguages = (pkg: PackageT) => {
  let language = 0;
  if (pkg.nodes && pkg.nodes.every((node) => node.language === 'cpp')) {
    language = 2;
  } else if (
    pkg.nodes &&
    pkg.nodes.every((node) => node.language === 'py') &&
    (!pkg.services || pkg.services.length == 0)
  ) {
    language = 1;
  }
  return language;
};

/**
 *
 * @param {JSZip} zip - JSZip for package
 * @param {number} language - number, 1 if python, 2 if c++, 0 if both
 */
const createPackageXml = (
  zip: JSZip,
  pkg: PackageT,
  workspaceId: V4UUID
): void => {
  let packageXmlText = getPackageXmlText(pkg, workspaceId);
  zip.file('package.xml', packageXmlText);
};

export const getPackageXmlText = (
  pkg: PackageT,
  workspaceId: V4UUID
): string => {
  let language = getPackageLanguages(pkg);
  let packageXmlText = `<?xml version="1.0"?>
<?xml-model 
  href="http://download.ros.org/schema/package_format3.xsd" 
  schematypens="http://www.w3.org/2001/XMLSchema"?>
<package format="3">
  <!-- Edit package configuration below -->
${
  pkg.customText.packageXmlConfig === ''
    ? `  <name>${toSnakeCase(pkg.name)}</name>
  <version>0.0.0</version>
  <description>TODO: Package description</description>
  <maintainer email="your@email.com">Name</maintainer>
  <license>TODO: License declaration</license>`
    : pkg.customText.packageXmlConfig
}
  <!-- End package configuration customization -->

  <buildtool_depend>rosidl_default_generators</buildtool_depend>
  <exec_depend>rosidl_default_runtime</exec_depend>
  <member_of_group>rosidl_interface_packages</member_of_group>
`;
  if (language == 0) {
    packageXmlText += `
  <buildtool_depend>ament_cmake</buildtool_depend>
  <buildtool_depend>ament_cmake_python</buildtool_depend>
  
${getPackageXmlDependencies(pkg, workspaceId)}
  
  <test_depend>ament_lint_auto</test_depend>
  <test_depend>ament_lint_common</test_depend>
  <test_depend>ament_cmake_pytest</test_depend>

  <!-- Add additional package.xml tags below -->
${pkg.customText.packageXmlExtraTags}
  <!-- End custom package.xml tags -->

  <export>
    <build_type>ament_cmake</build_type>
  </export>
</package>`;
  } else if (language == 1) {
    packageXmlText += `

  <buildtool_depend>ament_python</buildtool_depend>
  
${getPackageXmlDependencies(pkg, workspaceId)}

  <test_depend>ament_copyright</test_depend>
  <test_depend>ament_flake8</test_depend>
  <test_depend>ament_pep257</test_depend>
  <test_depend>python3-pytest</test_depend>

  <!-- Add additional package.xml tags below -->
${pkg.customText.packageXmlExtraTags}
  <!-- End custom package.xml tags -->

  <export>
    <build_type>ament_python</build_type>
  </export>
</package>
`;
  } else if (language == 2) {
    packageXmlText += `
  <buildtool_depend>ament_cmake</buildtool_depend>

${getPackageXmlDependencies(pkg, workspaceId)}

  <test_depend>ament_lint_auto</test_depend>
  <test_depend>ament_lint_common</test_depend>
  
  <!-- Add additional package.xml tags below -->
${pkg.customText.packageXmlExtraTags}
  <!-- End custom package.xml tags -->

  <export>
    <build_type>ament_cmake</build_type>
  </export>
</package>
`;
  }
  return packageXmlText;
};

/**
 * Gets the formatted string of dependencies for the package.xml file
 * @param pkg
 * @param workspaceId
 * @returns
 */
const getPackageXmlDependencies = (pkg: PackageT, workspaceId: V4UUID) => {
  return getPackageDependencies(pkg, workspaceId).reduce(
    (accumulator, dep) => (accumulator += `  <depend>${dep}</depend>\n`),
    ''
  );
};

////////////////////////////////////////////////////////////////////
//                         Python Package                         //
////////////////////////////////////////////////////////////////////
// NOTE: if setup.py deprecated warning, run "pip install setuptools==58.2.0"

/**
 * Creates a setup.py file in the base folder of the given JSZip folder.
 * only for use if the node contains only python files
 * @param {JSZip} zip - JSZip for package
 */
const createSetupPy = (zip: JSZip, pkg: PackageT): void => {
  const setupPyText = getSetupPyText(pkg);
  zip.file('setup.py', setupPyText);
};

/**
 * Gets the text for the setup.py file of the given package,
 * returns that text as a string.
 * only for use if the node contains only python files
 */
export const getSetupPyText = (pkg: PackageT): string => {
  let scripts = pkg.nodes.reduce(
    (accumulator: string, node: Node) =>
      accumulator +
      `            '${toSnakeCase(node.name)} = ${toSnakeCase(
        pkg.name
      )}.${toCamelCase(node.name)}:main',\n`,
    ''
  );
  return `
import os
from glob import glob
from setuptools import find_packages, setup

package_name = '${toSnakeCase(pkg.name)}'

setup(
    name=package_name,
    version='0.0.0',
    packages=find_packages(exclude=['test']),
    data_files=[
        # Install marker file in the package index
        ('share/ament_index/resource_index/packages', ['resource/' + package_name]),
        # Include our package.xml file
        (os.path.join('share', package_name), ['package.xml']),
        # Include all launch files.
        (os.path.join('share', package_name, 'launch'), glob(os.path.join('launch', '*launch.[pxy][yma]*'))),
    ],
    # customize setup.py configuration below
    install_requires=['setuptools'],
    zip_safe=True,
    maintainer='Name',
    maintainer_email='your@email.com',
    description='TODO: Package description',
    license='TODO: License declaration',
    tests_require=['pytest'],
    # end setup.py custom configuration
    entry_points={
        'console_scripts': [
${scripts}
          # add custom entry points below
          # end custom entry points
        ],
    },
)
    `;
};

/**
 * Creates the setup.cfg file, for use with python packages
 * @param {*} zip
 */
const createSetupCfg = (zip: JSZip, pkg: PackageT): void => {
  const setupCfgText = getSetupCfgText(pkg);
  zip.file('setup.cfg', setupCfgText);
};

export const getSetupCfgText = (pkg: PackageT): string => {
  return `
[develop]
script_dir=$base/lib/${toSnakeCase(pkg.name)}
[install]
install_scripts=$base/lib/${toSnakeCase(pkg.name)}
`;
};

////////////////////////////////////////////////////////////////////
//                           c++ Package                          //
////////////////////////////////////////////////////////////////////

const createCMakeLists = (
  zip: JSZip,
  pkg: PackageT,
  workspaceId: V4UUID
): void => {
  let cMakeListsText = getCMakeListsText(pkg, workspaceId);
  zip.file('CMakeLists.txt', cMakeListsText);
};

export const getCMakeListsText = (
  pkg: PackageT,
  workspaceId: V4UUID
): string => {
  let usingPy =
    pkg.nodes && !pkg.nodes.every((node) => node.language === 'cpp');
  // for installing cpp nodes
  const cppNodeNames: string = pkg.nodes
    .filter((node) => node.language === 'cpp')
    .reduce(
      (accumulator: string, node: Node) =>
        accumulator + ' ' + toCamelCase(node.name),
      ''
    );
  // for installing python nodes
  const pyNodeNames: string = pkg.nodes
    .filter((node) => node.language === 'py')
    .reduce(
      (accumulator: string, node: Node) =>
        accumulator +
        `${toSnakeCase(pkg.name)}/${toCamelCase(node.name)}.py \n`,
      ''
    );

  // formatted find_package imports
  let findPackagesStr: string = getPackageDependencies(pkg, workspaceId).reduce(
    (accumulator, pkgName) =>
      accumulator + `find_package(${toSnakeCase(pkgName)} REQUIRED)\n`,
    ''
  );
  // adding services to findPackagesStr

  // string of add_executable calls, each one followed by ament_target_dependencies for that node
  const executablesList: string = pkg.nodes.reduce(
    (accumulator: string, node: Node) => {
      const dependenciesStr: string = getNodeDependencies(node, workspaceId)
        .filter((dep) => dep !== pkg.name)
        .reduce(
          (depAccumulator: string, dependency: string) =>
            depAccumulator + ' ' + toSnakeCase(dependency),
          ''
        );
      if (node.language === 'cpp') {
        accumulator += `add_executable(${toCamelCase(
          node.name
        )} src/${toCamelCase(node.name)}.cpp)\n`;
        accumulator += `ament_target_dependencies(${toCamelCase(
          node.name
        )} ${dependenciesStr})\n`;
        if (
          pkg.services.find(
            (srv: Service) =>
              srv.clients.includes(node.id) || srv.server === node.id
          ) !== undefined
        ) {
          accumulator += `target_link_libraries(${toCamelCase(
            node.name
          )} "\${cpp_typesupport_target}")\n`;
        }
        accumulator += '\n';
      }
      return accumulator;
    },
    ''
  );

  // compiling services
  let serviceGenStr = '';
  if (pkg.services.length > 0)
    serviceGenStr += 'find_package(rosidl_default_generators REQUIRED)\n';
  const srvList: string = pkg.services.reduce(
    (accumulator: string, srv: Service) => {
      return accumulator + `    "srv/${toCamelCase(srv.name)}.srv"\n`;
    },
    ''
  );
  if (srvList.length > 0)
    serviceGenStr += `rosidl_generate_interfaces(\${PROJECT_NAME}\n${srvList}\n)\n`;
  // checking if any services are connected to cpp nodes
  if (
    pkg.services.some((srv: Service) => {
      return (
        srv.clients.find(
          (nodeId: V4UUID) =>
            getNodeDependency(workspaceId, nodeId) &&
            getNodeDependency(workspaceId, nodeId)?.node.language === 'cpp'
        ) ||
        (srv.server &&
          getNodeDependency(workspaceId, srv.server) &&
          getNodeDependency(workspaceId, srv.server)?.node.language === 'cpp')
      );
    })
  ) {
    serviceGenStr += `rosidl_get_typesupport_target(cpp_typesupport_target \${PROJECT_NAME} rosidl_typesupport_cpp)`;
  }

  return `
cmake_minimum_required(VERSION 3.8)
project(${toSnakeCase(pkg.name)})

if(CMAKE_COMPILER_IS_GNUCXX OR CMAKE_CXX_COMPILER_ID MATCHES "Clang")
    add_compile_options(-Wall -Wextra -Wpedantic)
endif()

# find dependencies
find_package(ament_cmake REQUIRED)
${
  usingPy ? 'find_package(ament_cmake_python REQUIRED)\n' : ''
}${findPackagesStr}
${serviceGenStr}
if(BUILD_TESTING)
    find_package(ament_lint_auto REQUIRED)
    # the following line skips the linter which checks for copyrights
    # comment the line when a copyright and license is added to all source files
    set(ament_cmake_copyright_FOUND TRUE)
    # the following line skips cpplint (only works in a git repo)
    # comment the line when this package is in a git repo and when
    # a copyright and license is added to all source files
    set(ament_cmake_cpplint_FOUND TRUE)
    ament_lint_auto_find_test_dependencies()
endif()
${
  usingPy
    ? `\nif(BUILD_TESTING)
find_package(ament_cmake_pytest REQUIRED)
set(_pytest_tests
  # Add test files here
)
foreach(_test_path \${_pytest_tests})
  get_filename_component(_test_name \${_test_path} NAME_WE)
  ament_add_pytest_test(\${_test_name} \${_test_path}
    APPEND_ENV PYTHONPATH=\${CMAKE_CURRENT_BINARY_DIR}
    TIMEOUT 60
    WORKING_DIRECTORY \${CMAKE_SOURCE_DIR}
  )
endforeach()
endif()\n`
    : ''
}

include_directories(
  include
  \${catkin_INCLUDE_DIRS}
)

${executablesList}

# Install launch files
install(
  DIRECTORY launch
  DESTINATION share/\${PROJECT_NAME}
)

# Install cpp nodes
install(
  TARGETS ${cppNodeNames}
  DESTINATION lib/\${PROJECT_NAME}
)

${
  usingPy
    ? `# Install Python executables\ninstall(PROGRAMS\n  ${pyNodeNames}\n  DESTINATION lib/\${PROJECT_NAME}\n)\n`
    : ''
}

# customize CMakeLists.txt below
${pkg.customText.cMakeListsText}
# end CMakeLists.txt customization

ament_package()
`;
};
